import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactSelect from "react-select";
import CurrencyInput from "react-currency-input-field";
import { toast } from "react-toastify";
import { getCustomersByBusiness } from "../../redux/reducers/customerSlice";
import { getInvoiceList } from "../../redux/reducers/invoiceSlice";
import { createPaymentLink } from "../../redux/reducers/transactionSlice";
import { IoIosSend } from "react-icons/io";
import { LuLink2, LuCopy, LuCheck, LuPlus } from "react-icons/lu";
import { Modal, Alert, Button } from "react-bootstrap";

const DirectPaymentLink = ({ onSuccess }) => {
    const dispatch = useDispatch();
    const baseURL = process.env.REACT_APP_BASEURL;
    const selectedBusiness = useSelector((state) => state.business.selectedBusiness);
    const activeBusinessId = useSelector((state) => state.business.activeBusinessId);
    const customerData = useSelector((state) => state.customer.customerList);
    const invoiceData = useSelector((state) => state.invoice.invoiceList);
    const { loading: txLoading, paymentLinkUrl } = useSelector((state) => state.transaction);

    const [show, setShow] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    const [copied, setCopied] = useState(false);
    const [generatedLink, setGeneratedLink] = useState(null);

    const handleClose = () => {
        setShow(false);
        setGeneratedLink(null);
        setCopied(false);
    };

    useEffect(() => {
        if (activeBusinessId) {
            dispatch(getCustomersByBusiness(activeBusinessId));
            dispatch(getInvoiceList({ businessId: activeBusinessId, page: 1, limit: 50 }));
        }
        if (baseURL) {
            fetch(baseURL + "/currency")
                .then((res) => res.json())
                .then((data) => setCurrencies(data.data || []));
        }
    }, [dispatch, activeBusinessId, baseURL]);

    const customerOptions = customerData?.map((e) => ({
        label: `${e.name} (${e.email})`,
        value: e._id,
        email: e.email,
        name: e.name,
    }));

    const currencyOptions = currencies?.map((e) => ({
        label: `${e.code} — ${e.name}`,
        value: e.code,
    }));

    const validationSchema = Yup.object().shape({
        customerId: Yup.string().required("Customer is required"),
        amount: Yup.number().positive("Amount must be positive").required("Amount is required"),
        currencyCode: Yup.string().required("Currency is required"),
        subject: Yup.string().max(100, "Subject too long").optional(),
        invoiceId: Yup.string().nullable(),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const customer = customerData.find((c) => c._id === values.customerId);
            const payload = {
                businessId: activeBusinessId,
                amount: values.amount,
                description: values.subject || "Direct Payment",
                customerEmail: customer?.email,
                customerName: customer?.name,
                currencyCode: values.currencyCode,
                invoiceId: values.invoiceId || undefined,
            };

            const res = await dispatch(createPaymentLink(payload)).unwrap();
            if (res?.url) {
                setGeneratedLink(res.url);
                toast.success("Payment link generated successfully!");
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            toast.error(error || "An error occurred");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = () => {
        if (generatedLink) {
            navigator.clipboard.writeText(generatedLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <>
            <Button
                variant="primary"
                className="d-flex align-items-center gap-2 fw-bold rounded-pill px-4"
                onClick={() => setShow(true)}
            >
                <LuPlus size={18} /> Create Payment Link
            </Button>

            <Modal show={show} onHide={handleClose} centered size="md" backdrop="static">
                <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <div className="p-2 rounded-3 bg-soft-brand text-brand">
                                <LuLink2 size={20} />
                            </div>
                            <Modal.Title className="fw-bold fs-5">Create Payment Link</Modal.Title>
                        </div>
                        <p className="text-muted small mb-0 ms-1">
                            Generate a Stripe link and send it directly to your customer.
                        </p>
                    </div>
                </Modal.Header>

                <Modal.Body className="px-4 pt-3 pb-4">
                    {!activeBusinessId && (
                        <Alert variant="warning" className="small py-2 border-0 rounded-3">
                            <strong>Note:</strong> Please select a business from the header first.
                        </Alert>
                    )}

                    {/* Success: Show generated link */}
                    {generatedLink ? (
                        <div className="text-center py-3">
                            <div className="text-success mb-3">
                                <LuCheck size={48} />
                            </div>
                            <h6 className="fw-bold mb-2">Payment Link Ready!</h6>
                            <p className="text-muted small mb-4">Share this link with your customer to collect payment.</p>
                            <div className="bg-light rounded-3 p-3 d-flex align-items-center gap-2 mb-4">
                                <span className="small text-truncate flex-grow-1 text-muted">{generatedLink}</span>
                                <Button
                                    variant={copied ? "success" : "outline-secondary"}
                                    size="sm"
                                    className="rounded-pill flex-shrink-0 d-flex align-items-center gap-1"
                                    onClick={handleCopy}
                                >
                                    {copied ? <><LuCheck size={14} /> Copied</> : <><LuCopy size={14} /> Copy</>}
                                </Button>
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="light" className="flex-grow-1 rounded-pill fw-bold" onClick={handleClose}>
                                    Close
                                </Button>
                                <a
                                    href={generatedLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-primary flex-grow-1 rounded-pill fw-bold"
                                >
                                    Open Link
                                </a>
                            </div>
                        </div>
                    ) : (
                        <Formik
                            initialValues={{
                                customerId: "",
                                amount: "",
                                currencyCode: "USD",
                                subject: "",
                                invoiceId: "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ setFieldValue, isSubmitting, values }) => {
                                const relevantInvoices = (invoiceData || [])
                                    .filter((inv) => {
                                        const custId = typeof inv.customer === "object" ? inv.customer?._id : inv.customer;
                                        return custId === values.customerId && inv.status !== "Paid";
                                    })
                                    .map((inv) => ({
                                        label: `Invoice #${inv._id.toString().slice(-6).toUpperCase()} — $${inv.totalAmount}`,
                                        value: inv._id,
                                        amount: inv.totalAmount,
                                    }));

                                return (
                                    <Form>
                                        <div className="mb-3">
                                            <label className="invoice-label">Customer <span className="text-danger">*</span></label>
                                            <ReactSelect
                                                options={customerOptions}
                                                placeholder="Search customer..."
                                                onChange={(opt) => {
                                                    setFieldValue("customerId", opt ? opt.value : "");
                                                    setFieldValue("invoiceId", "");
                                                }}
                                                isClearable
                                            />
                                            <ErrorMessage name="customerId" component="div" className="formik_red_txt mt-1 small" />
                                        </div>

                                        {values.customerId && relevantInvoices.length > 0 && (
                                            <div className="mb-3">
                                                <label className="invoice-label">Link to Unpaid Invoice <span className="text-muted small fw-normal">(Optional)</span></label>
                                                <ReactSelect
                                                    options={relevantInvoices}
                                                    placeholder="Select an invoice..."
                                                    isClearable
                                                    onChange={(opt) => {
                                                        setFieldValue("invoiceId", opt ? opt.value : "");
                                                        if (opt?.amount) {
                                                            setFieldValue("amount", opt.amount);
                                                            setFieldValue("subject", `Payment for Invoice #${opt.value.toString().slice(-6).toUpperCase()}`);
                                                        }
                                                    }}
                                                />
                                                <div className="text-muted small mt-1">Paying via this link will mark the invoice as Paid.</div>
                                            </div>
                                        )}

                                        <div className="row g-3 mb-3">
                                            <div className="col-7">
                                                <label className="invoice-label">Amount <span className="text-danger">*</span></label>
                                                <CurrencyInput
                                                    className="gbl-input w-100"
                                                    placeholder="0.00"
                                                    value={values.amount}
                                                    onValueChange={(value) => setFieldValue("amount", value)}
                                                    prefix="$"
                                                    decimalsLimit={2}
                                                />
                                                <ErrorMessage name="amount" component="div" className="formik_red_txt mt-1 small" />
                                            </div>
                                            <div className="col-5">
                                                <label className="invoice-label">Currency <span className="text-danger">*</span></label>
                                                <ReactSelect
                                                    options={currencyOptions}
                                                    placeholder="USD"
                                                    defaultValue={{ label: "USD — US Dollar", value: "USD" }}
                                                    onChange={(opt) => setFieldValue("currencyCode", opt ? opt.value : "")}
                                                />
                                                <ErrorMessage name="currencyCode" component="div" className="formik_red_txt mt-1 small" />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="invoice-label">Subject / Reference</label>
                                            <Field
                                                name="subject"
                                                className="gbl-input w-100"
                                                placeholder="e.g., Consulting Deposit, Project Milestone"
                                            />
                                        </div>

                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="w-100 py-2 fw-bold rounded-pill d-flex align-items-center justify-content-center gap-2"
                                            disabled={isSubmitting || txLoading}
                                        >
                                            <IoIosSend size={20} />
                                            {isSubmitting || txLoading ? "Generating..." : "Generate & Send Link"}
                                        </Button>
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default DirectPaymentLink;
